import React, {useCallback, useState} from 'react';
import {Button, Modal, Show, t, TextField} from '@ultradent/components';
import {translationKeys as _} from '@/constants/translations';
import {validateEmail} from '@ultradent/utilities/Strings';
import UserService from '@/providers/user';
import {ErrorCodes, ErrorMessages, getErrorList, getInvalidPostValues} from '@/util/Errors';
import invoke from 'lodash.invoke';
import {uniqList} from '@ultradent/utilities/Arrays';
import {AccountTypes, Roles} from '@/constants/appConstants';
import {PermissionOptionList} from '@/components/dialogs/ManageUserPermissions';
import {RolesOptionList} from '@/components/dialogs/ManageUserRole';
import {RegionOptionList} from '@/components/dialogs/ManageUserRegions';
import {FormikProvider, useFormik} from 'formik';
import {isEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';
import FormPostError from '@/components/common/Form/FormPostError';

// resource -react-tag-autocomplete Docs; https://www.npmjs.com/package/react-tag-autocomplete#autoresize-optional
const tokenInputClassNames = {
    root: 'react-tags md:flex flex-auto items-center',
    rootFocused: 'is-focused',
    selected: 'react-tags__selected',
    selectedTag: 'react-tags__selected-tag',
    selectedTagName: 'react-tags__selected-tag-name',
    search: 'react-tags__search min-w-2xs flex-auto',
    searchWrapper: 'react-tags__search-wrapper',
    searchInput: 'react-tags__search-input text-field ghosted w-full',
    suggestions: 'react-tags__suggestions',
    suggestionActive: 'is-active',
    suggestionDisabled: 'is-disabled'
};
// Remove trailing period - some OSs will add a period at end of string on double press of [SPACE] key
const sanitizeTagString = ( value ) => value.trim().replace( /(.*)\.$/, '$1' );
const hasInvalidTag = ( tags ) => tags.filter( tag => !tag.isValid ).length > 0;
const hasValidInput = query => validateEmail( sanitizeTagString( query ) ) || !query;
const getValidUserNames = ( tags ) => tags.filter( tag => tag.isValid ).map( tag => tag.name );
const labelRequired = `(${t( 'label.required', _.label.required )})`;

export const InviteUsers = ( props ) => {
    const [isProcessing, setIsProcessing] = useState( props.processing );
    const [formPostError, setFormPostError] = useState( null );
    const [transientPermissions, setTransientPermissions] = useState( [] );
    const isKOL = props.crmAccountTypeId === AccountTypes.KOL;

    const formik = useFormik( {
        enableReinitialize: true,
        validateOnChange: true,
        validate: ( values ) => {
            const errors = {};

            if ( !validateEmail( values.userEmail ) ) {
                errors.userEmail = t( 'notify.invalidEmail', _.notify.invalidEmail );
            }
            if ( values.permissions.length <= 0 ) {
                errors.permissions = t(
                    'userManagement.notify.atLeastOnePermissionRequired',
                    _.userManagement.notify.atLeastOnePermissionRequired
                )
            }
            return errors;
        },
        initialValues: {
            permissions: [],
            role: Roles.MEMBER,
            userEmail: '',
            regions: []
        },
        onSubmit: async ( values ) => {
            setFormPostError( null );
            const errors = await formik.validateForm( values );

            if ( !isProcessing && isEmptyOrNil( errors ) ) {
                setIsProcessing( true );

                try {
                    const {userEmail, role, permissions, regions} = values;
                    const securityGroups = uniqList( [role, permissions, isKOL && Roles.KOL] ).filter( Boolean );
                    const resp = await UserService.sendInvite( {
                        crmAccount: props.crmAccountId,
                        regions,
                        users: [userEmail],
                        securityGroups
                    } );
                    invoke( props, 'onInviteSuccess', resp );
                }
                catch ( err ) {
                    handleRequestError( err );
                }
                finally {
                    setIsProcessing( false );
                }
            }

            return false;
        }
    } );

    const handlePermissionChange = useCallback( ( value ) => {
            setTransientPermissions( formik.values.permissions );
        },
        [formik.values]
    );

    const handleRoleChange = useCallback( ( value ) => {
            const isAdminRole = value === Roles.PARTNER_ADMIN;

            if ( isAdminRole ) {
                setTransientPermissions( formik.values.permissions );
            }

            formik.setFieldValue( 'permissions', isAdminRole
                                                 ? props.availableFeatures
                                                 : transientPermissions );
        },
        [formik.values, props.availableFeatures]
    );

    function handleRequestError ( err ) {
        if ( err.response?.data?.code ) {
            parseRequestErrorResponse( err.response.data );
            return;
        }
        setFormPostError( {
            message: ErrorMessages.UnknownError
        } );
    }

    function parseRequestErrorResponse ( err ) {
        switch ( err.code ) {
            case ErrorCodes.Validation:
            case ErrorCodes.Invitation:
            case ErrorCodes.AccountAlreadyExists:
            case ErrorCodes.InvitationUserExistsOtherAccount:
                handleValidationError( err );
                break;

            default:
                setFormPostError( {
                    message: ErrorMessages.UnknownError
                } );
        }
    }

    function handleValidationError ( err ) {
        const invalidValues = getInvalidPostValues( err.data );

        setFormPostError( {
            message: err.message,
            errorList: getErrorList( err.data )
        } );
    }

    return (
        <Modal
            title={t( 'userManagement.label.inviteFormTitle', _.userManagement.label.inviteFormTitle )}
            size="md"
            onClose={props.onClose}
            isOpen={props.isOpen}>
            <FormikProvider value={formik}>
                <form onSubmit={formik.handleSubmit}>
                    <Show when={!isKOL}>
                        <h3 className="subheading-base flex justify-between">
                            <span>{t( 'userManagement.label.role', _.userManagement.label.role )}</span>
                            <span className="text-body-sm font-normal">{labelRequired}</span>
                        </h3>
                        <RolesOptionList onChange={handleRoleChange}/>
                    </Show>
                    <h3 className="subheading-base flex justify-between">
                        <span>{t( 'userManagement.label.sitePermissions',
                            _.userManagement.label.sitePermissions )}</span>
                        <span className="text-body-sm font-normal">{labelRequired}</span>
                    </h3>
                    <PermissionOptionList
                        onChange={handlePermissionChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.values.role === Roles.PARTNER_ADMIN}
                        error={formik.touched.permissions && formik.errors.permissions}
                        availableFeatures={props.availableFeatures}/>
                    <h3 className="subheading-base">{t( 'userManagement.label.preferredRegion',
                        _.userManagement.label.preferredRegion )}</h3>
                    <RegionOptionList
                        onBlur={formik.handleBlur}
                        error={formik.touched.regions && formik.errors.regions}/>
                    {formPostError &&
                        <FormPostError {...formPostError} />
                    }

                    <TextField
                        name="userEmail"
                        placeholder={t( 'label.emailAddressHint', _.label.emailAddressHint )}
                        labelClassName="w-full"
                        value={formik.values.userEmail}
                        error={formik.touched.userEmail && formik.errors.userEmail}
                        onChange={formik.handleChange}
                        label={
                            <div className="mb-4">
                                <h3 className="subheading-base flex justify-between">
                                    <span>{t( 'label.emailAddress', _.label.emailAddress )}</span>
                                    <span className="text-body-sm font-normal">{labelRequired}</span>
                                </h3>
                                <p className="leading-tight text-grey-darker mb-2 text-caption">{t(
                                    'userManagement.label.inviteEmailCaption',
                                    _.userManagement.label.inviteEmailCaption
                                )}</p>
                            </div>
                        }
                    />

                    <Button className="button w-full flex justify-self-end md:w-auto mt-8"
                            type="submit"
                            loading={isProcessing}
                            disabled={isProcessing}>
                        {isProcessing
                         ? t( 'actionLabel.sending', _.actionLabel.sending )
                         : t( 'actionLabel.invite', _.actionLabel.invite )
                        }
                    </Button>
                </form>
            </FormikProvider>
        </Modal>
    )
        ;
}

InviteUsers.defaultProps = {
    accountType: AccountTypes.OTHER,
    isOpen: false,
    onClose: () => {},
    tags: [],
    availableFeatures: []
}
